/**
 * Issue #759 — step 3 of 3, the saved step: the echo, "what the record now
 * holds", the season goal quoted back, and the reflection as the third
 * step. Copy strings and small pure helpers, pulled out of the component
 * for the same reason `logScorecardStep.js` (#758) gives: this repo mounts
 * no components in unit tests, so anything worth pinning down directly
 * lives here instead, where a plain Jest test can read it with no DOM at
 * all.
 *
 * Source: `Saved.dc.html` (design-round-5, sha256 `20b26ed3…`), the
 * approved drawing for this step — its own inline copy is the source of
 * truth; where the issue text and the drawing differ, the drawing wins
 * (brief-759.md, Step 0). The title rule is the one deliberate exception
 * (brief-759.md's own ruling): the drawing only ever shows the one-match
 * case, so the "otherwise" title comes from the issue text, not the
 * drawing.
 */

export const STEP_COUNTER_LABEL = "Step 3 of 3";

export const PROGRESS_STEP_1 = "1 Ground";
export const PROGRESS_STEP_2 = "2 The figures";
export const PROGRESS_STEP_3 = "3 A reflection";

export const EYEBROW = "On the record";
// Saved.dc.html:79 — the drawn (and only) state: the record's first match.
export const TITLE_FIRST = "Your first match on the record";
// From the issue text, not the drawing (brief-759.md's Step 0, "Rules for
// the words that vary" — the drawing never shows a player with more than
// one match, so this half of the rule has no drawn counterpart to match
// byte-for-byte).
export const TITLE_LATER = "Your match is on the record";
export const LEDE = "Here it is, exactly as you gave it. Nothing added.";

export const BATTING_LABEL = "Batting";
export const BOWLING_LABEL = "Bowling";
export const RUNS_LABEL = "Runs";
export const BALLS_LABEL = "Balls";
export const OUT_LABEL = "Out";
export const OVERS_LABEL = "Overs";
export const WKTS_LABEL = "Wkts";

export const DID_NOT_BOWL_NOTE =
  "You didn't bowl. It stays blank — the record won't call that a zero.";

// D152 (#884, Andreas, 2026-09-25, decisions-2026-09-24.md; review round,
// #913): `LogMatchSavedStep.jsx` drops the whole season-goal quote-back
// (`log-saved-goal`, both states) this round, and the five copy constants
// that used to render it (`GOAL_HEADING`, `GOAL_HELP`, `NO_GOAL_LINE`,
// `NO_GOAL_LINK`, `NO_GOAL_LINK_TO`) are deleted, not kept dead — dead
// exports are a maintenance trap (nothing catches one going stale). If a
// future structured-goal "achieved" moment wants this exact copy again,
// `Saved.dc.html:111/133` (design-round-5) still draws it verbatim.

export const REFLECT_BADGE = "3";
export const REFLECT_HEADING = "Add a reflection";
export const REFLECT_SUB = "A few lines on how it went, while it is still fresh.";
export const REFLECT_HELP =
  "Step three of three — and you can still skip it. The match is already saved; nothing is missing from the record if you walk away here.";
export const REFLECT_CTA = "Write the reflection";

export const SKIP_LABEL = "Skip it — see what the record holds";
export const SKIP_SUB = "You can add one later, from the match.";

/**
 * Issue #759, brief-759.md D57 fold-in — the SAME failure copy
 * `ReflectionPrompt`'s toast already used (`PerformanceTracking.jsx`),
 * shown inline instead once the reflection is written from the saved step:
 * a failure here stays on the page rather than raising a toast (D57
 * applies to the reflection's own failure path on this screen too, per the
 * brief's own ruling).
 */
export const REFLECT_SAVE_FAILED =
  "Couldn't save the reflection — your match is logged either way. Try again.";

/**
 * Issue #1029 (#842 part B), `SavedRating.dc.html` (design-round-6b, D204.1):
 * the optional rating of the field or net picked on step 2, on its own save.
 * The noun follows #544's vocabulary: the kind in words where somebody has
 * said what the surface is, a neutral "surface" where nobody has.
 */
export const RATE_ROW_TITLE = (noun) => `Rate this ${noun} (optional)`;
export const RATE_ROW_SUB = (name) =>
  `How ${name} played, scored 1 to 5. Others see the average, never who gave it.`;
export const RATE_OPEN_TITLE = (noun) => `Rate this ${noun}`;
export const RATE_HIDE = "Hide";
export const RATE_OPTIONAL_LINE = "Optional. Your match is already saved; the rating saves on its own.";
export const RATE_SAVE = "Save the rating";
export const RATE_SAVING = "Saving…";
export const RATE_CLEAR = "Clear rating";
export const RATE_SAVED = (name) => `Rating saved for ${name}.`;
export const RATE_CHANGE = "Change it";
export const RATE_FAILED =
  "Match saved — rating not saved. You can rate this field or net from Grounds, or try again.";

export function rateNoun(kind) {
  return kind === "net" ? "net" : kind === "field" ? "field" : "surface";
}

/**
 * Saved.dc.html:79 vs. the issue text's own "otherwise" branch — see this
 * file's header. `matchesCount` is `GET /stats/summary`'s own `matches`,
 * taken AFTER the just-saved match is already counted in it.
 */
export function titleFor(matchesCount) {
  return matchesCount === 1 ? TITLE_FIRST : TITLE_LATER;
}

/** "one" for 1 (Saved.dc.html:106's own drawn word), a numeral from 2 up
 * (brief-759.md, "Rules for the words that vary"). */
export function matchCountWord(n) {
  return n === 1 ? "one" : String(n);
}

/** "match" for 1, "matches" from 2 up. */
export function matchNoun(n) {
  return n === 1 ? "match" : "matches";
}

function present(v) {
  return v !== null && v !== undefined && v !== "";
}

/**
 * The "record now holds" sentence — brief-759.md's own rule:
 *
 *   "The record now holds {n} {match|matches}: {runs} off {balls},
 *   {dismissal in lower case}, at {ground}."
 *
 * Leaves out any absent part CLEANLY (never a dash mid-sentence, never a
 * trailing "off" with nothing in front of it): `runs`/`balls` collapse to
 * "34" alone when there is no divisor to name (no balls faced), and drop
 * out of the sentence entirely when there are no runs to report at all —
 * "off 41" with nothing batted is not a sentence fragment this app has any
 * data to justify. `dismissal` is lower-cased (`Caught` -> `caught`);
 * `ground` renders as its own "at {ground}" clause. Any part that is
 * itself absent is left out of the joined list, not replaced with a dash —
 * the dash is the TILE's mark (`counterText`), not this sentence's.
 *
 * Unit-tested for every combination, and for every part absent on its own
 * (brief-759.md, "Tests").
 *
 * PR #844 review, B3 — `matches` is `GET /stats/summary`'s own count,
 * fetched over the network AFTER the match itself is already safely
 * saved (`PerformanceTracking.jsx`'s `create()`); that fetch can fail
 * independently of the save, and `matches` then arrives here as
 * `undefined`. The original version of this function fed it straight into
 * `matchCountWord`/`matchNoun`, which have no "unknown" case at all — the
 * first arm of `matchCountWord` is a strict `n === 1`, so `undefined` fell
 * through to `String(undefined)`, and the rendered sentence read "The
 * record now holds undefined matches: …", on a real player-visible screen.
 * When the count is unknown, the whole "N match(es)" CLAUSE is left out —
 * not replaced with a dash, not guessed at "at least one" — and the
 * sentence leads with `UNKNOWN_COUNT_HEAD` instead: "Saved to the record:
 * 5 off 9, lbw, at [ground]." `countKnown` is a real type check
 * (`typeof matches === "number" && Number.isFinite(matches)`), not a
 * truthiness test — a genuinely reported `0` (unlikely right after a
 * save, but not impossible) is a KNOWN count and takes the normal branch,
 * same as any other number; only `null`/`undefined`/a non-numeric value
 * is "unknown". Unit-tested for `undefined`, `null` and `0` explicitly.
 */
const UNKNOWN_COUNT_HEAD = "Saved to the record";

export function buildHoldsSentence({ matches, runs, balls, dismissal, ground }) {
  const battingFigure = present(runs)
    ? (present(balls) ? `${runs} off ${balls}` : String(runs))
    : null;
  const dismissalPart = present(dismissal) ? String(dismissal).toLowerCase() : null;
  const groundPart = present(ground) ? `at ${ground}` : null;

  const parts = [battingFigure, dismissalPart, groundPart].filter((p) => p !== null);
  const countKnown = typeof matches === "number" && Number.isFinite(matches);
  const head = countKnown ? `The record now holds ${matchCountWord(matches)} ${matchNoun(matches)}` : UNKNOWN_COUNT_HEAD;
  return parts.length === 0 ? `${head}.` : `${head}: ${parts.join(", ")}.`;
}

/**
 * Issue #759 §1 — "when nothing was bowled, one line under the bowling
 * tiles". Nothing bowled is every bowling counter absent, the same
 * null-check every figure on this screen already uses (`counterOrNull`'s
 * own truthiness test, `lib/counters.js`) — not merely "overs is blank",
 * since a spell with overs recorded but (implausibly) every other figure
 * blank is not "nothing bowled".
 */
export function bowledNothing(bowling) {
  const b = bowling || {};
  return !present(b.overs) && !present(b.runs_conceded) && !present(b.wickets);
}
